import React, { useState, useEffect } from "react";
import Sidebar from "../Sidebar/Sidebar";
import Navbar from "./Navbar";
import Header from "./header";
import SaveAsIcon from "@mui/icons-material/SaveAs";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import ApiConfig from "../../common/config/ApiConfig";
import { formatNumber } from "../../common/utils/helper";
import { useLanguage } from "../../common/i18n/LanguageContext";

// lotto_type codes must match LottoType.type exactly (e.g. "Yeekee_5", not
// "yeekee_5") — /setfightlimit resolves valid stakes for the type via
// getStakeFieldsForLottoType, which looks the type up by that exact string.
//
// getLottoTypes only returns { id, name, type, startTime, endTime, status }
// — no styling — so the header bar color stays a local lookup keyed by
// `type`, merged onto the fetched list at render time. A lotto type not
// listed here still renders, just with the fallback style.
const BOX_STYLE_BY_TYPE = {
  th_gov: { bgClass: "bg-blue-400" },
  laos_gov: { bgClass: "bg-blue-700" },
  hanoy_PhuQua: { bgClass: "bg-red-400" },
  Yeekee_5: { bgClass: "bg-orange-300" },
  Yeekee_10: { bgClass: "bg-orange-700" },
  Yeekee_15: { bgClass: "bg-orange-500" },
};
const DEFAULT_BOX_STYLE = { bgClass: "bg-slate-500" };

// getLottoRewardTypeGroups returns [{ id, name, rewardTypes: [{ id, name,
// code, stake, exp }], lottoType: { id, name, type, ... } }, ...] — groups
// for the same lotto type are merged into one stake-field list, keyed by
// lottoType.type (e.g. "th_gov"), each field labeled by the reward type's
// own name and keyed by its code (the value /setfightlimit's newfightlimit
// object and /getfightlimit's rows use as `stake`).
const buildStakesFieldByType = (rewardTypeGroups) =>
  (rewardTypeGroups || []).reduce((acc, group) => {
    const type = group.lottoType?.type;
    if (!type) return acc;
    const existingCodes = new Set((acc[type] || []).map((f) => f.code));
    const fields = (group.rewardTypes || [])
      .filter((rt) => !existingCodes.has(rt.code))
      .map((rt) => ({
        code: rt.code,
        label: rt.name,
      }));
    acc[type] = [...(acc[type] || []), ...fields];
    return acc;
  }, {});

// Rows are stake-pivoted — one per (agent, lotto_type, stake), e.g.
// { id, agent, lotto_type: "th_gov", stake: "first", fight_limit: "1000.00" }
// — same shape /getfightlimit's fightlimt/fightlimtUpline arrays and
// /setfightlimit's response array all use.
const findFightLimit = (list, type, stake) =>
  (list || []).find((r) => r.lotto_type === type && r.stake === stake);

function Fightlimit() {
  const { t } = useLanguage();
  const [lottoTypes, setLottoTypes] = useState([]);
  const [fightlimitList, setFightlimitList] = useState([]);
  const [fightlimitUplineList, setFightlimitUplineList] = useState([]);
  const [rewardTypeGroups, setRewardTypeGroups] = useState([]);
  const [updates, setUpdates] = useState({});
  const [selectedType, setSelectedType] = useState("");

  const user = localStorage.getItem("user");
  const [currencyCode, setCurrencyCode] = useState("");
  const currencySuffix = currencyCode ? ` (${currencyCode})` : "";

  useEffect(() => {
    (async () => {
      const [list, profile] = await Promise.all([
        ApiConfig.get("/getCurrencyList"),
        ApiConfig.get(`/agentprofile?user=${encodeURIComponent(user)}`),
      ]);
      if (list.resCode !== 200 || profile.resCode !== 200) return;
      const currency = (list.data ?? []).find(
        (c) => c.id === profile.data?.currency_id,
      );
      setCurrencyCode(currency?.code ?? "");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    ApiConfig.get("/getLottoTypes", {}).then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      setLottoTypes(Array.isArray(result.data) ? result.data : []);
    });
    ApiConfig.get("/getLottoRewardTypeGroups").then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      setRewardTypeGroups(Array.isArray(result.data) ? result.data : []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Default the filter to the first lotto type once the list loads.
  useEffect(() => {
    if (!selectedType && lottoTypes.length > 0) {
      setSelectedType(lottoTypes[0].type);
    }
  }, [lottoTypes, selectedType]);

  const stakesFieldByType = buildStakesFieldByType(rewardTypeGroups);

  const boxes = lottoTypes.map((lt) => ({
    type: lt.type,
    title: lt.name,
    stakes: stakesFieldByType[lt.type] || [],
    ...(BOX_STYLE_BY_TYPE[lt.type] || DEFAULT_BOX_STYLE),
  }));

  const activeBox = boxes.find((box) => box.type === selectedType);

  const fightlimitget = async () => {
    await ApiConfig.get(
      `/getfightlimit?agent=${encodeURIComponent(user)}`,
    ).then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      const list = Array.isArray(result.data?.fightlimt)
        ? result.data.fightlimt
        : [];
      const uplineList = Array.isArray(result.data?.fightlimtUpline)
        ? result.data.fightlimtUpline
        : [];
      setFightlimitList(list);
      setFightlimitUplineList(uplineList);
      setUpdates(
        boxes.reduce((acc, box) => {
          acc[box.type] = box.stakes.reduce((stakeAcc, s) => {
            const row = findFightLimit(list, box.type, s.code);
            stakeAcc[s.code] = row ? Number(row.fight_limit) : 0;
            return stakeAcc;
          }, {});
          return acc;
        }, {}),
      );
    });
  };

  // getLottoTypes/getLottoRewardTypeGroups resolve async — wait for both so
  // this doesn't fetch fight limits (and build `updates`) against an empty
  // box/stakes list on first mount.
  useEffect(() => {
    if (lottoTypes.length === 0 || rewardTypeGroups.length === 0) return;
    fightlimitget();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lottoTypes, rewardTypeGroups]);

  // A downline agent's fight limit can never exceed their own upline's
  // fight limit for that stake (fightlimtUpline, from /getfightlimit).
  const handleChange = (type, stake, rawValue) => {
    const uplineRow = findFightLimit(fightlimitUplineList, type, stake);
    const cap = uplineRow ? Number(uplineRow.fight_limit) : undefined;
    let value = Number(rawValue);
    if (isNaN(value) || value < 0) value = 0;
    if (cap !== undefined && value > cap) {
      value = cap;
      Swal.fire({
        title: t("member_commission_validate_title"),
        text: `${t("fightlimit_exceed_prefix")} ${formatNumber(cap)}`,
        icon: "error",
      });
    }
    setUpdates((prev) => ({
      ...prev,
      [type]: { ...prev[type], [stake]: value },
    }));
  };

  const handlesave = (type) => {
    const json = {
      agent: user,
      type,
      newfightlimit: updates[type] || {},
    };
    ApiConfig.post("/setfightlimit", json)
      .then((result) => {
        if (result.resCode === 200) {
          toast.success(t("fightlimit_update_success"));
          fightlimitget();
        } else {
          toast.error(result.message || t("fightlimit_update_error"));
        }
      })
      .catch(() => {
        toast.error(t("fightlimit_update_error"));
      });
  };

  const pos = localStorage.getItem("position");

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <div className="shrink-0">
        <Navbar />
      </div>
      <div className="flex flex-1 overflow-hidden items-start">
        <div className="shrink-0 overflow-y-auto h-full">
          <Sidebar pos={pos} />
        </div>
        <div className="flex flex-col flex-1 max-h-full overflow-hidden">
          <div className="shrink-0">
            <Header title={t("fightlimit_col_max")} />
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            <div className="flex items-center gap-2 mb-2">
              <label className="font-bold text-xs">
                {t("commission_filter_by_lotto_type")}
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="border border-slate-300 rounded px-2 py-1 text-xs"
              >
                {lottoTypes.map((lt) => (
                  <option key={lt.id} value={lt.type}>
                    {lt.name}
                  </option>
                ))}
              </select>
            </div>

            {activeBox && (
              <div className={`w-full ${activeBox.bgClass} p-2 rounded-sm`}>
                <div>
                  <span className="text-white text-xs font-bold">
                    {activeBox.title}
                  </span>
                </div>

                <div className="transition-all duration-500">
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className="bg-blue-200 text-xs py-1">
                          {t("fightlimit_col_game_type")}
                        </th>
                        <th className="bg-blue-200 text-xs py-1">
                          {t("membertable_tx_col_amount")}
                          {currencySuffix}
                        </th>
                        <th className="bg-blue-200 text-xs py-1">
                          {t("fightlimit_col_max")}
                          {currencySuffix}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {activeBox.stakes.map((s) => {
                        const currentRow = findFightLimit(
                          fightlimitList,
                          activeBox.type,
                          s.code,
                        );
                        const uplineRow = findFightLimit(
                          fightlimitUplineList,
                          activeBox.type,
                          s.code,
                        );
                        return (
                          <tr key={s.code}>
                            <td className="p-1 text-xs">{s.label}</td>
                            <td className="px-2 py-1">
                              <div className="flex flex-col">
                                {uplineRow && (
                                  <span className="text-[10px] text-amber-600 font-bold">
                                    {t("commission_max_label")}{" "}
                                    {formatNumber(uplineRow.fight_limit)}
                                  </span>
                                )}
                                <input
                                  value={updates[activeBox.type]?.[s.code] ?? 0}
                                  onChange={(e) =>
                                    handleChange(
                                      activeBox.type,
                                      s.code,
                                      e.target.value,
                                    )
                                  }
                                  min={0}
                                  max={
                                    uplineRow
                                      ? Number(uplineRow.fight_limit)
                                      : undefined
                                  }
                                  type="number"
                                  onClick={(e) => e.target.select()}
                                  className="w-[100%] px-2 py-1 text-xs text-gray-700 border rounded-lg focus:outline-none focus:border-blue-500"
                                />
                              </div>
                            </td>
                            <td className="p-1 text-xs">
                              {currentRow
                                ? formatNumber(currentRow.fight_limit)
                                : ""}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="mt-1 p-1 mb-1 bg-gradient-to-b from-brand-gold-300 via-brand-gold-400 to-brand-gold-600 w-full text-center text-brand-ink-onGold rounded border border-brand-gold-200 shadow-gold hover:brightness-105 ">
                    <button
                      className="w-full"
                      onClick={() => handlesave(activeBox.type)}
                    >
                      <SaveAsIcon className="ml-1 text-[8px] md:text-[1px]" />
                      <span className="ml-1 w-full text-[8px] md:text-[10px]">
                        {t("fightlimit_save_button")}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Fightlimit;
